/**
 * Ramp Viewer
 * Renders a 3D ramp using Three.js based on configuration changes
 */

(function() {
  'use strict';

  let scene, camera, renderer, controls, ramp, rampWireframe, ground, stepBlock;
  let animationId = null;
  let cameraInitialized = false; // Track ob Kamera bereits initialisiert wurde
  // Szene-Maße (cm)
  const WOOD_TILE_CM = 20;           // ungefährer Holz-"Tile" Maßstab
  const FRONT_MARGIN_CM = 30;       // 1m vor der Rampe
  const STEP_TOP_DEPTH_CM = 50;     // 1m nach der Stufe (auf dem oberen Podest)
  const STEP_GAP_CM = 1;             // Abstand Rampe -> Stufe
  const GROUND_SIDE_MARGIN_CM = 20;  // Rand links/rechts neben Rampe
  const GROUND_MIN_WIDTH_CM = 120;   // Mindestbreite der Platte
  const GROUND_CORNER_RADIUS_CM = 30;

  function createRoundedRectShape(width, height, radius) {
    const w = width;
    const h = height;
    const r = Math.max(0, Math.min(radius, Math.min(w, h) / 2));
    const x = -w / 2;
    const y = -h / 2;

    const shape = new THREE.Shape();
    shape.moveTo(x + r, y);
    shape.lineTo(x + w - r, y);
    shape.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
    shape.lineTo(x + w, y + h - r);
    shape.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
    shape.lineTo(x + r, y + h);
    shape.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
    shape.lineTo(x, y + r);
    shape.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
    return shape;
  }

  // Planare UVs für ShapeGeometry, damit die Holztextur sauber liegt.
  function applyPlanarUV(geometry, width, height) {
    geometry.computeBoundingBox();
    const pos = geometry.attributes.position;
    const uvs = new Float32Array(pos.count * 2);
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      uvs[i * 2 + 0] = (x / width) + 0.5;
      uvs[i * 2 + 1] = (y / height) + 0.5;
    }
    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  }

  function init() {
    const viewers = document.querySelectorAll('.ramp-viewer');
    
    if (viewers.length === 0) {
      return;
    }

    // Load Three.js and OrbitControls
    loadThreeJS(() => {
      viewers.forEach(viewer => {
        setupViewer(viewer);
      });
    });
  }

  function loadThreeJS(callback) {
    // Check if Three.js is already loaded
    if (typeof THREE !== 'undefined') {
      loadOrbitControls(callback);
      return;
    }

    // Load Three.js as ES module to support OrbitControls
    const threeScript = document.createElement('script');
    threeScript.type = 'module';
    threeScript.textContent = `
      import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.module.js';
      window.THREE = THREE;
      window.dispatchEvent(new CustomEvent('threejs-loaded'));
    `;
    document.head.appendChild(threeScript);
    
    window.addEventListener('threejs-loaded', () => {
      loadOrbitControls(callback);
    }, { once: true });
    
    // Timeout fallback
    setTimeout(() => {
      if (typeof THREE === 'undefined') {
        console.error('Failed to load Three.js as module, trying fallback');
        const fallbackScript = document.createElement('script');
        fallbackScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js';
        fallbackScript.onload = () => {
          loadOrbitControls(callback);
        };
        fallbackScript.onerror = () => {
          console.error('Failed to load Three.js');
        };
        document.head.appendChild(fallbackScript);
      }
    }, 2000);
  }

  function loadOrbitControls(callback) {
    // Check if OrbitControls is already loaded
    if (typeof THREE !== 'undefined' && THREE.OrbitControls) {
      callback();
      return;
    }
    if (window.OrbitControls) {
      callback();
      return;
    }

    // Wait for THREE to be available (with timeout)
    let attempts = 0;
    const maxAttempts = 50;
    const checkThree = () => {
      attempts++;
      if (typeof THREE !== 'undefined') {
        // Check if importmap is supported
        const hasImportMap = document.querySelector('script[type="importmap"]');
        
        if (hasImportMap) {
          // Load OrbitControls using ES module with importmap
          const orbitScript = document.createElement('script');
          orbitScript.type = 'module';
          orbitScript.textContent = `
            import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
            window.OrbitControls = OrbitControls;
            console.log('OrbitControls loaded via importmap');
            window.dispatchEvent(new CustomEvent('orbitcontrols-loaded'));
          `;
          document.head.appendChild(orbitScript);
        } else {
          // Load OrbitControls with explicit full URL
          const orbitScript = document.createElement('script');
          orbitScript.type = 'module';
          orbitScript.textContent = `
            import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/jsm/controls/OrbitControls.js';
            import * as THREE_MODULE from 'https://cdn.jsdelivr.net/npm/three@0.128.0/build/three.module.js';
            window.OrbitControls = OrbitControls;
            console.log('OrbitControls loaded via direct URL');
            window.dispatchEvent(new CustomEvent('orbitcontrols-loaded'));
          `;
          document.head.appendChild(orbitScript);
        }
        
        // Listen for load event
        window.addEventListener('orbitcontrols-loaded', () => {
          console.log('OrbitControls loaded successfully');
          callback();
        }, { once: true });
        
        // Fallback after timeout
        setTimeout(() => {
          if (!window.OrbitControls) {
            console.warn('OrbitControls not loaded after timeout, trying fallback');
            // Try direct import as fallback
            const fallbackScript = document.createElement('script');
            fallbackScript.type = 'module';
            fallbackScript.textContent = `
              import { OrbitControls } from 'https://cdn.jsdelivr.net/npm/three@0.128.0/examples/jsm/controls/OrbitControls.js';
              window.OrbitControls = OrbitControls;
              window.dispatchEvent(new CustomEvent('orbitcontrols-loaded'));
            `;
            document.head.appendChild(fallbackScript);
            setTimeout(() => {
              if (!window.OrbitControls) {
                console.warn('OrbitControls not available, camera controls disabled');
                callback();
              }
            }, 2000);
          }
        }, 3000);
      } else if (attempts < maxAttempts) {
        setTimeout(checkThree, 100);
      } else {
        console.warn('THREE.js not available after timeout, continuing without OrbitControls');
        callback();
      }
    };
    checkThree();
  }

  function setupViewer(viewer) {
    const sectionId = viewer.dataset.sectionId;
    const productId = viewer.dataset.productId;

    if (!sectionId) {
      console.warn('Ramp viewer missing data-section-id');
      return;
    }

    // Show loading indicator
    viewer.setAttribute('data-loading', 'true');
    viewer.classList.remove('loaded');

    // Initialize Three.js scene
    initScene(viewer);

    // Debounce timer for 3D updates to improve performance
    let updateRampTimeout = null;
    let lastConfig = null;
    
    // Listen for configuration changes
    document.addEventListener('rw:config-change', function(event) {
      if (event.detail.sectionId === sectionId) {
        const config = { ...event.detail.config, sectionId: sectionId };
        
        // Clear existing timeout
        if (updateRampTimeout) {
          clearTimeout(updateRampTimeout);
        }
        
        // Debounce: wait 100ms before updating 3D view
        // Only update if config actually changed
        const configKey = `${config.height}_${config.length}_${config.width}_${config.colorHex || config.color}`;
        const lastConfigKey = lastConfig ? `${lastConfig.height}_${lastConfig.length}_${lastConfig.width}_${lastConfig.colorHex || lastConfig.color}` : '';
        
        if (configKey !== lastConfigKey) {
          updateRampTimeout = setTimeout(() => {
            lastConfig = config;
            updateRamp(config);
          }, 100);
        }
      }
    });

    // Initial load with default config
    setTimeout(() => {
      const configurator = document.querySelector(`.ramp-configurator[data-section-id="${sectionId}"]`);
      if (configurator) {
        const heightSlider = configurator.querySelector('[data-ramp-param="height"]');
        const widthSlider = configurator.querySelector('[data-ramp-param="width"]');
        const lengthSlider = configurator.querySelector('[data-ramp-param="length"]');
        const colorInput = configurator.querySelector('input[data-ramp-param="color"]');
        const manufacturerSelect = configurator.querySelector('[data-ramp-param="robot-manufacturer"]');
        const modelSelect = configurator.querySelector('[data-ramp-param="robot-model"]');
        
        if (heightSlider && widthSlider && lengthSlider && colorInput) {
          const initialConfig = {
            height: parseFloat(heightSlider.value) || 10,
            width: parseFloat(widthSlider.value) || 50,
            length: parseFloat(lengthSlider.value) || 30,
            color: colorInput.value || 'black',
            robotManufacturer: manufacturerSelect ? manufacturerSelect.value : '',
            robotModel: modelSelect ? modelSelect.value : '',
            sectionId: sectionId
          };
          updateRamp(initialConfig);
        } else {
          updateRamp({ height: 10, width: 50, length: 30, color: 'black', sectionId: sectionId });
        }
      } else {
        updateRamp({ height: 10, width: 50, length: 30, color: 'black', sectionId: sectionId });
      }
    }, 300);
  }

  // Create wood texture using canvas
  function createWoodTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    
    // Base wood color - darker warm brown (dunkleres Holz)
    const baseColor = { r: 120, g: 95, b: 70 }; // Noch dunkleres braun
    
    // Fill with base color
    ctx.fillStyle = `rgb(${baseColor.r}, ${baseColor.g}, ${baseColor.b})`;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add wood grain pattern
    for (let i = 0; i < canvas.height; i += 2) {
      const variation = Math.sin(i / 20) * 15 + Math.random() * 10;
      const r = Math.max(0, Math.min(255, baseColor.r + variation));
      const g = Math.max(0, Math.min(255, baseColor.g + variation * 0.7));
      const b = Math.max(0, Math.min(255, baseColor.b + variation * 0.5));
      
      ctx.strokeStyle = `rgb(${Math.floor(r)}, ${Math.floor(g)}, ${Math.floor(b)})`;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(canvas.width, i);
      ctx.stroke();
    }
    
    // Add darker grain lines for depth
    for (let i = 0; i < 20; i++) {
      const y = Math.random() * canvas.height;
      const darkness = 20 + Math.random() * 30;
      ctx.strokeStyle = `rgba(${Math.max(0, baseColor.r - darkness)}, ${Math.max(0, baseColor.g - darkness)}, ${Math.max(0, baseColor.b - darkness)}, 0.6)`;
      ctx.lineWidth = 1 + Math.random() * 2;
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
    
    // Add some knots/imperfections
    for (let i = 0; i < 5; i++) {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const radius = 5 + Math.random() * 10;
      const darkness = 40 + Math.random() * 40;
      
      const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
      gradient.addColorStop(0, `rgba(${Math.max(0, baseColor.r - darkness)}, ${Math.max(0, baseColor.g - darkness)}, ${Math.max(0, baseColor.b - darkness)}, 0.8)`);
      gradient.addColorStop(1, `rgba(${Math.max(0, baseColor.r - darkness)}, ${Math.max(0, baseColor.g - darkness)}, ${Math.max(0, baseColor.b - darkness)}, 0)`);
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    
    // Create texture from canvas
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;
    return texture;
  }

  function initScene(container) {
    if (typeof THREE === 'undefined') {
      console.error('Three.js not loaded');
      return;
    }

    scene = new THREE.Scene();

    // Use light gray background #e0e0e0 (RGB: 224, 224, 224)
    // Setze die Farbe explizit als RGB-Werte
    const bgColor = new THREE.Color();
    bgColor.setRGB(224 / 255, 224 / 255, 224 / 255);
    scene.background = bgColor;

    // Get container dimensions - wait a bit for layout to settle
    const getContainerSize = () => {
      // Force layout recalculation on mobile
      const rect = container.getBoundingClientRect();
      const width = rect.width || container.clientWidth || container.offsetWidth || 800;
      const height = rect.height || container.clientHeight || container.offsetHeight || 600;
      return { width: Math.max(width, 100), height: Math.max(height, 100) };
    };
    
    // Wait for container to have proper size (especially important on mobile)
    const ensureContainerSize = (callback) => {
      let attempts = 0;
      const maxAttempts = 20;
      const checkSize = () => {
        attempts++;
        const size = getContainerSize();
        if (size.width > 100 && size.height > 100) {
          callback(size);
        } else if (attempts < maxAttempts) {
          requestAnimationFrame(checkSize);
        } else {
          // Fallback: use window size or defaults
          const fallbackSize = {
            width: Math.max(window.innerWidth * 0.9, 400),
            height: Math.max(window.innerHeight * 0.5, 400)
          };
          callback(fallbackSize);
        }
      };
      // Start checking after a short delay to let layout settle
      setTimeout(() => {
        requestAnimationFrame(checkSize);
      }, 50);
    };
    
    let size = getContainerSize();
    let aspect = size.width / size.height;

    // Initialize camera and renderer with proper size
    const isMobile = window.innerWidth < 768 || ('ontouchstart' in window);
    const fov = isMobile ? 65 : 50; // Größerer FOV = mehr sichtbar
    
    camera = new THREE.PerspectiveCamera(fov, aspect, 0.1, 5000);
    // Start position - will be adjusted when ramp is created
    camera.position.set(80, 40, 100);
    camera.lookAt(0, 10, 0);

    renderer = new THREE.WebGLRenderer({ 
      antialias: true,
      alpha: false,
      powerPreference: "high-performance"
    });
    renderer.setSize(size.width, size.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Prevent pixelation on high DPI
    // Setze explizit die Hintergrundfarbe des Renderers ZUERST
    // Verwende RGB-Werte direkt (224, 224, 224) = #e0e0e0
    renderer.setClearColor(0xe0e0e0, 1);
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Weiche Schatten
    renderer.shadowMap.autoUpdate = true;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    container.appendChild(renderer.domElement);
    
    // Re-check size after a delay and update if needed (important for mobile)
    ensureContainerSize((finalSize) => {
      if (finalSize.width !== size.width || finalSize.height !== size.height) {
        size = finalSize;
        aspect = size.width / size.height;
        camera.aspect = aspect;
        camera.updateProjectionMatrix();
        renderer.setSize(size.width, size.height);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      }
    });
    
    // Stelle sicher, dass der Canvas-Hintergrund die richtige Farbe hat
    const canvas = renderer.domElement;
    canvas.style.backgroundColor = '#e0e0e0';
    
    // Mark canvas as loaded after a short delay
    setTimeout(() => {
      renderer.domElement.classList.add('loaded');
    }, 100);

    // Lights - etwas mehr Kontrast + weichere Schatten für mehr Tiefe
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.25);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
    // Etwas flacherer Winkel -> sichtbarere Schatten
    directionalLight.position.set(220, 260, 140);
    directionalLight.castShadow = true;
    directionalLight.shadow.mapSize.width = 4096; // Höhere Auflösung für bessere Schatten
    directionalLight.shadow.mapSize.height = 4096;
    directionalLight.shadow.camera.left = -500;
    directionalLight.shadow.camera.right = 500;
    directionalLight.shadow.camera.top = 500;
    directionalLight.shadow.camera.bottom = -500;
    directionalLight.shadow.camera.near = 0.5;
    directionalLight.shadow.camera.far = 1000;
    directionalLight.shadow.bias = -0.0001; // Reduziert Shadow Acne
    directionalLight.shadow.normalBias = 0.02;
    directionalLight.shadow.radius = 3; // Weichere Schattenkanten
    scene.add(directionalLight);
    
    // Zusätzliches weiches Licht für bessere Beleuchtung - reduziert
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.25);
    fillLight.position.set(-180, 180, -220);
    scene.add(fillLight);

    // Weiches "Himmelslicht" für natürlichere Aufhellung
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xb0b0b0, 0.25);
    hemiLight.position.set(0, 300, 0);
    scene.add(hemiLight);

    // Ground plane with wood texture
    // Wird später in updateRamp dynamisch an Rampen-/Stufenmaße angepasst,
    // hier nur ein initiales "Placeholder"-Mesh.
    const initialW = 240;
    const initialH = 240;
    const groundShape = createRoundedRectShape(initialW, initialH, Math.min(GROUND_CORNER_RADIUS_CM, initialW / 2, initialH / 2));
    const groundGeometry = new THREE.ShapeGeometry(groundShape, 32);
    applyPlanarUV(groundGeometry, initialW, initialH);
    
    // Create wood texture using canvas
    const woodTexture = createWoodTexture();
    woodTexture.wrapS = THREE.RepeatWrapping;
    woodTexture.wrapT = THREE.RepeatWrapping;
    // Konstanter "Realwelt"-Maßstab für die Maserung (ungefähr WOOD_TILE_CM pro Tile)
    woodTexture.repeat.set(initialW / WOOD_TILE_CM, initialH / WOOD_TILE_CM);
    
    const groundMaterial = new THREE.MeshStandardMaterial({ 
      map: woodTexture,
      roughness: 0.8,
      metalness: 0.0
    });
    ground = new THREE.Mesh(groundGeometry, groundMaterial);
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -0.1;
    ground.userData.width = initialW;
    ground.userData.length = initialH;
    ground.receiveShadow = true;
    scene.add(ground);

    // Controls - try window.OrbitControls first (ES module), then THREE.OrbitControls (UMD)
    // Wait a bit for OrbitControls to load if not immediately available
    const initControls = () => {
      const OrbitControlsClass = window.OrbitControls || (typeof THREE !== 'undefined' && THREE.OrbitControls);
      if (OrbitControlsClass) {
        try {
          controls = new OrbitControlsClass(camera, renderer.domElement);
          controls.enableDamping = true;
          controls.dampingFactor = 0.05;
          controls.enablePan = true;
          controls.enableZoom = true;
          controls.enableRotate = true;
          controls.minDistance = 30;
          controls.maxDistance = 300;
          controls.target.set(0, 10, 0);
          controls.update();
          console.log('OrbitControls initialized successfully');
          return true;
        } catch (error) {
          console.error('Error initializing OrbitControls:', error);
          return false;
        }
      }
      return false;
    };
    
    if (!initControls()) {
      // Retry after a short delay
      setTimeout(() => {
        if (!initControls()) {
          console.warn('OrbitControls not available - camera rotation disabled');
        }
      }, 500);
    }

    // Handle window resize
    const handleResize = () => {
      const newSize = getContainerSize();
      if (newSize.width > 0 && newSize.height > 0) {
        const newAspect = newSize.width / newSize.height;
        if (Math.abs(camera.aspect - newAspect) > 0.01 || 
            Math.abs(renderer.getSize(new THREE.Vector2()).width - newSize.width) > 1) {
          camera.aspect = newAspect;
          camera.updateProjectionMatrix();
          renderer.setSize(newSize.width, newSize.height);
          renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        }
      }
    };
    window.addEventListener('resize', handleResize);
    
    // Also handle container resize (for dynamic height changes)
    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        // Debounce resize to avoid excessive updates
        requestAnimationFrame(handleResize);
      });
      resizeObserver.observe(container);
    }
    
    // Additional check after initial render (important for mobile layout)
    setTimeout(() => {
      handleResize();
    }, 200);

    // Animation loop
    function animate() {
      animationId = requestAnimationFrame(animate);
      if (controls) controls.update();
      // Stelle sicher, dass die Hintergrundfarbe bei jedem Frame gesetzt ist
      if (scene.background) {
        renderer.setClearColor(scene.background, 1);
      }
      renderer.render(scene, camera);
    }
    animate();
  }

  // Get max incline for a robot model (same logic as in ramp-configurator.js)
  function getMaxIncline(manufacturer, modelValue) {
    if (!manufacturer || !modelValue) {
      return 20; // Default: 20 Grad
    }
    
    // Schätzung basierend auf Jahr und Hersteller
    // Premium-Modelle haben oft bessere Steigfähigkeit
    if (modelValue.includes('s9') || modelValue.includes('s8') || 
        modelValue.includes('x1') || modelValue.includes('max') || 
        modelValue.includes('pro') || modelValue.includes('ultra') ||
        modelValue.includes('saros')) {
      return 25;
    }
    
    // Standard neuere Modelle (2020+)
    if (modelValue.includes('j9') || modelValue.includes('j8') || 
        modelValue.includes('j7') || modelValue.includes('i7') ||
        modelValue.includes('t20') || modelValue.includes('t10') ||
        modelValue.includes('g10') || modelValue.includes('s7')) {
      return 22;
    }
    
    // Standard für alle anderen
    return 20;
  }

  // Calculate incline in degrees
  // Die Steigung wird auf 0,5° aufgerundet
  function calculateIncline(height, length) {
    if (!length || length === 0) return 0;
    // Steigung in Grad = arctan(Höhe/Länge) * 180/π
    const inclineRad = Math.atan(height / length);
    const inclineDeg = (inclineRad * 180) / Math.PI;
    // Runde auf 0,5° auf (z.B. 18,3° → 18,5°, 18,7° → 19,0°)
    return Math.ceil(inclineDeg * 2) / 2;
  }


  function updateRamp(config) {
    if (!scene) {
      console.warn('Scene not initialized yet');
      return;
    }

    const height = config.height || 10;
    const width = config.width || 50;
    const depth = config.length || 30; // depth = length

    // Bodenplatte dynamisch: 1m vor der Rampe + 1m hinter der Stufe (oben).
    if (ground && ground.material && ground.material.map) {
      const rampLowEdgeZ = -depth / 2;
      const rampHighEdgeZ = depth / 2;
      const stepFrontFaceZ = rampHighEdgeZ + STEP_GAP_CM;
      const stepEndZ = stepFrontFaceZ + STEP_TOP_DEPTH_CM;

      const frontEdgeZ = rampLowEdgeZ - FRONT_MARGIN_CM;
      const backEdgeZ = stepEndZ;
      const groundLength = Math.max(60, backEdgeZ - frontEdgeZ);
      const groundWidth = Math.max(GROUND_MIN_WIDTH_CM, width + (GROUND_SIDE_MARGIN_CM * 2));
      const radius = Math.min(
        GROUND_CORNER_RADIUS_CM,
        Math.max(0, (groundWidth / 2) - 1),
        Math.max(0, (groundLength / 2) - 1)
      );

      const groundCenterZ = (frontEdgeZ + backEdgeZ) / 2;

      // Update geometry (rounded rectangle) + UVs
      const newGroundShape = createRoundedRectShape(groundWidth, groundLength, radius);
      const newGroundGeometry = new THREE.ShapeGeometry(newGroundShape, 32);
      applyPlanarUV(newGroundGeometry, groundWidth, groundLength);
      if (ground.geometry) ground.geometry.dispose();
      ground.geometry = newGroundGeometry;

      // Position + metadata
      ground.position.z = groundCenterZ;
      ground.userData.width = groundWidth;
      ground.userData.length = groundLength;
      ground.userData.centerZ = groundCenterZ;
      ground.userData.zMin = frontEdgeZ;
      ground.userData.zMax = backEdgeZ;
      ground.userData.stepFrontFaceZ = stepFrontFaceZ;

      // Texture scaling: world-ish tile size
      ground.material.map.repeat.set(groundWidth / WOOD_TILE_CM, groundLength / WOOD_TILE_CM);
      ground.material.map.needsUpdate = true;
    }
    

    // Get color from config - use hex color if available, otherwise fallback to colorMap
    let rampColor = 0x0a0a0a; // Default: sehr dunkles Schwarz
    
    if (config.colorHex) {
      // Konvertiere Hex-String zu Three.js Color (0xRRGGBB)
      const hex = config.colorHex.replace('#', '');
      rampColor = parseInt(hex, 16);
      
      // Wenn die Farbe sehr dunkel/schwarz ist, hellere sie etwas auf für bessere Sichtbarkeit
      const r = (rampColor >> 16) & 0xFF;
      const g = (rampColor >> 8) & 0xFF;
      const b = rampColor & 0xFF;
      
      // Prüfe ob die Farbe sehr dunkel ist (alle Werte < 30)
      if (r < 30 && g < 30 && b < 30) {
        // Erhöhe alle Werte um 20 für bessere Sichtbarkeit (aber bleibe dunkel)
        const newR = Math.min(255, r + 20);
        const newG = Math.min(255, g + 20);
        const newB = Math.min(255, b + 20);
        rampColor = (newR << 16) | (newG << 8) | newB;
      }
    } else {
      // Fallback: Color mapping für bessere Sichtbarkeit
      const colorMap = {
        'grey': 0x404040,      // Dunkleres Grau
        'black': 0x1a1a1a,     // Aufgehelltes Schwarz für bessere Sichtbarkeit (statt 0x0a0a0a)
        'white': 0xe0e0e0,     // Leicht graues Weiß
        'brown': 0x5c2e0a      // Dunkleres Braun
      };
      rampColor = colorMap[config.color] || 0x1a1a1a;
    }

    // Remove existing ramp and wireframe
    if (ramp) {
      scene.remove(ramp);
      if (ramp.geometry) ramp.geometry.dispose();
      if (ramp.material) ramp.material.dispose();
    }
    if (rampWireframe) {
      scene.remove(rampWireframe);
      // WireframeGeometry hat eine .geometry property, die die ursprüngliche Geometrie referenziert
      if (rampWireframe.geometry) {
        // Wenn es eine WireframeGeometry ist, dispose auch die referenzierte Geometrie
        if (rampWireframe.geometry.geometry) {
          rampWireframe.geometry.geometry.dispose();
        }
        rampWireframe.geometry.dispose();
      }
      if (rampWireframe.material) rampWireframe.material.dispose();
    }
    if (stepBlock) {
      scene.remove(stepBlock);
      if (stepBlock.geometry) stepBlock.geometry.dispose();
      if (stepBlock.material) {
        if (Array.isArray(stepBlock.material)) {
          stepBlock.material.forEach(m => {
            if (m && m.map && m.map.dispose) m.map.dispose();
            if (m && m.dispose) m.dispose();
          });
        } else if (stepBlock.material.dispose) {
          if (stepBlock.material.map && stepBlock.material.map.dispose) stepBlock.material.map.dispose();
          stepBlock.material.dispose();
        }
      }
    }

    // Create wedge/prism shape (Keilform)
    // Rampe liegt flach: Tiefe in Z-Richtung, Breite in X-Richtung, Höhe in Y-Richtung
    // Niedrige Kante bei z = -depth/2 (y = 0)
    // Hohe Kante bei z = depth/2 (y = height)

    const vertices = [];
    const indices = [];

    // 6 Vertices für einen Keil
    // Niedrige Kante (y = 0, z = -depth/2) - vorne
    vertices.push(-width/2, 0, -depth/2); // 0: links vorne unten
    vertices.push(width/2, 0, -depth/2);   // 1: rechts vorne unten

    // Hohe Kante unten (y = 0, z = depth/2) - hinten unten
    vertices.push(-width/2, 0, depth/2);    // 2: links hinten unten
    vertices.push(width/2, 0, depth/2);    // 3: rechts hinten unten

    // Hohe Kante oben (y = height, z = depth/2) - hinten oben
    vertices.push(-width/2, height, depth/2); // 4: links hinten oben
    vertices.push(width/2, height, depth/2);   // 5: rechts hinten oben

    // Indices für alle Flächen (korrekte Keilform mit richtiger Winding Order)
    // Wichtig: Counter-clockwise von außen gesehen für korrekte Normals in Three.js
    
    // Untere Fläche (Boden - Rechteck, von unten gesehen, counter-clockwise)
    indices.push(0, 1, 3);
    indices.push(0, 3, 2);

    // Obere schräge Fläche (Rampe - Trapez, von oben gesehen, counter-clockwise)
    indices.push(0, 4, 5);
    indices.push(0, 5, 1);

    // Vordere Fläche (niedrige Kante - Trapez, von vorne gesehen)
    // Die vordere Fläche verbindet die niedrige Kante (0,1) mit der oberen Kante (4,5)
    // Von vorne gesehen (aus negativer Z-Richtung), counter-clockwise für korrekte Normals:
    // Punkt 0 (links unten) -> Punkt 1 (rechts unten) -> Punkt 5 (rechts oben hinten) -> Punkt 4 (links oben hinten)
    // Aufgeteilt in zwei Dreiecke mit korrekter Winding Order:
    indices.push(0, 1, 5); // Erstes Dreieck: 0 -> 1 -> 5 (counter-clockwise von vorne)
    indices.push(0, 5, 4); // Zweites Dreieck: 0 -> 5 -> 4 (counter-clockwise von vorne)

    // Hintere Fläche (hohe Kante - Rechteck, von hinten gesehen, counter-clockwise)
    indices.push(2, 3, 5);
    indices.push(2, 5, 4);

    // Linke Seitenfläche (Dreieck, von links gesehen, counter-clockwise)
    indices.push(0, 2, 4);

    // Rechte Seitenfläche (Dreieck, von rechts gesehen, counter-clockwise)
    indices.push(1, 3, 5);

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geometry.setIndex(indices);
    
    // Compute normals with correct face orientation
    geometry.computeVertexNormals();
    
    // Manually fix normals for front face if needed
    // Get the normal attribute
    const normalAttribute = geometry.getAttribute('normal');
    if (normalAttribute) {
      const normals = normalAttribute.array;
      // Vertices 0, 1, 4, 5 are part of the front face
      // Ensure their normals point outward (positive Z direction for front face)
      // This is a simplified fix - in practice, we'd need to check each face
    }
    
    // Ensure correct face orientation
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    // Hauptmaterial - dunkler und weniger reflektierend
    const material = new THREE.MeshStandardMaterial({
      color: rampColor,
      roughness: 0.8,  // Höhere Roughness = weniger Glanz
      metalness: 0.0,
      flatShading: false,
      side: THREE.DoubleSide // Beide Seiten rendern, um invertierte Faces zu vermeiden
    });

    ramp = new THREE.Mesh(geometry, material);
    ramp.castShadow = true;
    ramp.receiveShadow = true;
    scene.add(ramp);

    // Wireframe/Gitternetz hinzufügen für bessere Geometrie-Sichtbarkeit
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: 0x000000,  // Schwarze Linien
      linewidth: 1,
      opacity: 0.4,
      transparent: true
    });
    
    // Erstelle Wireframe-Geometrie (geklont, damit sie unabhängig ist)
    const wireframeGeometry = geometry.clone();
    const wireframe = new THREE.WireframeGeometry(wireframeGeometry);
    rampWireframe = new THREE.LineSegments(wireframe, wireframeMaterial);
    rampWireframe.position.copy(ramp.position);
    rampWireframe.rotation.copy(ramp.rotation);
    scene.add(rampWireframe);

    // --- Stufe / Block (zur besseren Vorstellung) ---
    // Anforderungen:
    // - Höhe der Stufe = Rampenhöhe
    // - Oberseite = gleicher Bodenbelag (Holztextur) wie unten
    // - Stoßfläche (zur Rampe hin) weiß
    // - Rampe steht mit hoher Stirnseite ~1cm vor der Stufe
    // - Stufe so breit wie die Bodenfläche
    try {
      const groundWidth = (ground && ground.userData && ground.userData.width) || Math.max(GROUND_MIN_WIDTH_CM, width + (GROUND_SIDE_MARGIN_CM * 2));
      const stepHeight = Math.max(0.5, height); // mindestens sichtbar

      // Die Rampe hat ihre hohe Kante bei z = +depth/2
      const rampHighEdgeZ = depth / 2;
      const stepFrontFaceZ = rampHighEdgeZ + STEP_GAP_CM; // Stoßfläche der Stufe (zur Rampe hin)
      const stepDepth = STEP_TOP_DEPTH_CM; // 1m "nach der Stufe"
      const stepCenterZ = stepFrontFaceZ + (stepDepth / 2);

      const stepGeometry = new THREE.BoxGeometry(groundWidth, stepHeight, stepDepth);

      // Holztextur für die Oberseite: Weltmaßstab ähnlich zum Boden halten
      const topWoodTexture = (ground && ground.material && ground.material.map && ground.material.map.clone)
        ? ground.material.map.clone()
        : null;
      if (topWoodTexture) {
        topWoodTexture.wrapS = THREE.RepeatWrapping;
        topWoodTexture.wrapT = THREE.RepeatWrapping;
        topWoodTexture.repeat.set(
          Math.max(1, groundWidth / WOOD_TILE_CM),
          Math.max(1, stepDepth / WOOD_TILE_CM)
        );
        topWoodTexture.needsUpdate = true;
      }

      const woodSideMat = new THREE.MeshStandardMaterial({
        color: 0x785f46, // Holzton passend zur Textur-Basis
        roughness: 0.9,
        metalness: 0.0
      });
      const topMat = new THREE.MeshStandardMaterial({
        map: topWoodTexture || null,
        color: topWoodTexture ? 0xffffff : 0xdddddd,
        roughness: 0.85,
        metalness: 0.0
      });

      // BoxGeometry Material-Reihenfolge: +x, -x, +y(top), -y(bottom), +z, -z(front)
      // Stoßseite zur Rampe hin ist die -Z Fläche (Index 5) -> ebenfalls Holzton
      const stepMaterials = [woodSideMat, woodSideMat, topMat, woodSideMat, woodSideMat, woodSideMat];
      stepBlock = new THREE.Mesh(stepGeometry, stepMaterials);
      stepBlock.position.set(0, stepHeight / 2, stepCenterZ);
      stepBlock.castShadow = true;
      stepBlock.receiveShadow = true;
      scene.add(stepBlock);
    } catch (e) {
      console.warn('Failed to create step block:', e);
    }

    // Calculate optimal camera position based on ramp size
    // Convert cm to Three.js units (1 unit = 1 cm)
    const maxDimension = Math.max(width, depth, height);
    const distance = maxDimension * 2.2; // Distance multiplier
    const cameraHeight = height * 0.5 + maxDimension * 0.6;
    
    // Detect mobile viewport
    const isMobile = window.innerWidth < 768 || ('ontouchstart' in window);
    
    // Update FOV for mobile (wider view = more visible)
    if (isMobile && camera) {
      camera.fov = 65; // Größerer FOV für Mobile
      camera.updateProjectionMatrix();
    }
    
    // Nur beim ersten Mal die Kamera-Position setzen
    // Danach behält der Benutzer seine gewählte Ansicht
    if (!cameraInitialized) {
      // Ansicht: weniger direkt, mehr von oben-vorne-links
      // Mobile: etwas weiter weg und höher für bessere Übersicht
      const cameraX = isMobile ? -maxDimension * 1.8 : -maxDimension * 1.5;
      const cameraY = isMobile ? maxDimension * 1.8 : maxDimension * 1.5;
      const cameraZ = isMobile ? -maxDimension * 1.5 : -maxDimension * 1.2;
      
      camera.position.set(cameraX, cameraY, cameraZ);
      // Blickpunkt: etwas vor der Rampe, damit der Blickwinkel weniger direkt ist
      camera.lookAt(maxDimension * 0.2, height * 0.2, -maxDimension * 0.1);
      cameraInitialized = true;
    }
    
    // Update controls target and limits (wichtig für Zoom-Bereiche)
    if (controls) {
      // Blickpunkt etwas tiefer, damit die Ansicht wie im Referenzbild ist
      controls.target.set(0, height * 0.3, 0);
      // Mobile: größerer Zoom-Bereich
      const minDist = isMobile ? Math.max(maxDimension * 1.0, 80) : maxDimension * 1.2;
      const maxDist = isMobile ? Math.max(maxDimension * 3.5, 350) : maxDimension * 4;
      controls.minDistance = minDist;
      controls.maxDistance = maxDist;
      controls.update();
    }
    
    // Hide loading indicator
    const container = document.querySelector('.ramp-viewer');
    if (container) {
      container.removeAttribute('data-loading');
      // Also hide the ::before pseudo-element by adding a class
      container.classList.add('loaded');
    }
    
    // Mark canvas as loaded
    if (renderer && renderer.domElement) {
      renderer.domElement.classList.add('loaded');
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    if (animationId) {
      cancelAnimationFrame(animationId);
    }
    if (ramp) {
      if (ramp.geometry) ramp.geometry.dispose();
      if (ramp.material) ramp.material.dispose();
    }
    if (rampWireframe) {
      if (rampWireframe.geometry) rampWireframe.geometry.dispose();
      if (rampWireframe.material) rampWireframe.material.dispose();
    }
    if (ground) {
      if (ground.geometry) ground.geometry.dispose();
      if (ground.material) ground.material.dispose();
    }
    if (stepBlock) {
      if (stepBlock.geometry) stepBlock.geometry.dispose();
      if (stepBlock.material) {
        if (Array.isArray(stepBlock.material)) {
          stepBlock.material.forEach(m => {
            if (m && m.map && m.map.dispose) m.map.dispose();
            if (m && m.dispose) m.dispose();
          });
        } else if (stepBlock.material.dispose) {
          if (stepBlock.material.map && stepBlock.material.map.dispose) stepBlock.material.map.dispose();
          stepBlock.material.dispose();
        }
      }
    }
    if (renderer) {
      renderer.dispose();
    }
  });
})();
